import { ArrowRightIcon } from "@phosphor-icons/react/ArrowRight";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { BulletList } from "@freightsimple/ui-core/Typography/BulletList";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Callout } from "@freightsimple/ui-core/Display/Callout";
import { Divider } from "@freightsimple/ui-core/ComponentLayout/Divider";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import { FormItem } from "@freightsimple/ui-core/FormLayout/FormItem";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { ShowFormErrors } from "@freightsimple/ui-core/FormLayout/ShowFormErrors";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { TextArea } from "@freightsimple/ui-core/Controls/TextArea";
import { T } from "@freightsimple/ui-core/Typography/T";
import { Currency } from "@freightsimple/generated-dashboard-openapi-client/models/Currency";
import PageTitle from "@/Shared/Layout/PageTitle";
import PageLoadingSpinner from "@/Shared/Components/CustomComponents/PageLoadingSpinner";
import ShipmentNotFound from "@/Shared/Shipment/CustomComponents/ShipmentNotFound";
import { LoadFailed } from "@/Shared/Components/LoadFailed";
import useQuery from "@/Hooks/useQuery";
import { sumOf } from "@/Helpers/sumOf";
import { claimsFieldWidth } from "./Helpers/claimsFieldWidth";
import { ClaimsSummary } from "./CustomComponents/ClaimsSummary";
import { ClaimsFormLayout } from "./CustomComponents/ClaimsFormLayout";
import { ClaimsDamageClaimLineItemsSection } from "./CustomComponents/ClaimsDamageClaimLineItemsSection";
import { ClaimsSubmitClaimSection } from "./CustomComponents/ClaimsSubmitClaimSection";
import { ClaimsUploadClaimDocumentsSection } from "./CustomComponents/ClaimsUploadClaimDocumentsSection";
import {
  MAX_TOTAL_FILE_SIZE_MB,
  useClaimsSubmitClaim,
} from "./Hooks/useClaimsSubmitClaim";

const currencyOptions = [
  { value: Currency.Cad, label: "CAD" },
  { value: Currency.Usd, label: "USD" },
];

export function SubmitClaimScreen() {
  const query = useQuery();
  const shipmentIdParam = query["shipmentId"];
  const shipmentId = typeof shipmentIdParam === "string" ? shipmentIdParam : "";

  const claim = useClaimsSubmitClaim(shipmentId);
  const {
    shipment,
    existingClaims,
    forceValidation,
    claimLineItems,
    currency,
  } = claim;
  const claimDisabled = existingClaims.length > 0;

  // Before the loading gate: a shipment that 404s never arrives, so checking
  // this second left the not-found case spinning forever.
  if (claim.shipmentNotFound) {
    return <ShipmentNotFound />;
  }

  if (claim.loadFailed) {
    return <LoadFailed onRetry={claim.retryLoad} />;
  }

  if (!shipment || claimLineItems.length === 0) {
    return <PageLoadingSpinner />;
  }

  function calculateTotalClaim() {
    if (!claim.isValid()) {
      return undefined;
    }

    return sumOf(claimLineItems, (o) =>
      o.amountPerPiece === undefined || o.affectedPieces === undefined
        ? 0
        : o.amountPerPiece * o.affectedPieces,
    );
  }

  const totalClaim = calculateTotalClaim();

  return (
    <>
      <PageTitle>Submit Claim</PageTitle>
      <ClaimsFormLayout
        title="Submit Claim"
        description="We're sorry to hear there were problems. We're here to make getting compensation for your loss easy and efficient."
      >
        <Stack gap={8} align="stretch">
          <ClaimsSummary shipment={shipment} />
          <Divider />
          {claimDisabled ? (
            <Banner variant="warning" title="Claim Already Submitted">
              A claim has already been submitted for this shipment. You cannot
              submit additional claims.
            </Banner>
          ) : (
            <Banner variant="info" title="Notes on submitting a claim">
              <BulletList>
                <li>
                  An inspection may be required. Please do not dispose of the
                  freight to allow for an inspection to take place.
                </li>
                <li>
                  Please enter detailed and correct information. This will help
                  ensure the claim is settled quickly.
                </li>
                <li>
                  Please keep the total size of all uploaded files to{" "}
                  {MAX_TOTAL_FILE_SIZE_MB}MB or less. If your files are too
                  large, consider compressing them.
                </li>
              </BulletList>
            </Banner>
          )}

          {/* The rhythm between sections lives here, so no section can drift
                out of step by carrying its own margin. */}
          <Stack gap={16} align="stretch">
            <ClaimsSubmitClaimSection
              index={1}
              title="Claim Currency"
              description="What currency are the goods valued in?"
              content={
                <Dropdown
                  data-testid="claim-currency-dropdown"
                  value={currency}
                  onChange={(v) => claim.setCurrency(v as Currency)}
                  options={currencyOptions}
                  disabled={claimDisabled}
                  width={claimsFieldWidth.token}
                />
              }
            />
            <ClaimsDamageClaimLineItemsSection
              index={2}
              shipment={shipment}
              currency={currency}
              claimLineItems={claimLineItems}
              setClaimLineItems={claim.setClaimLineItems}
              forceValidation={forceValidation}
              disabled={claimDisabled}
            />
            <ClaimsSubmitClaimSection
              index={3}
              title="Original Invoice"
              description="Vendor’s original invoice for the shipment showing the price of the claimed goods"
              content={
                <ClaimsUploadClaimDocumentsSection
                  id="original-invoices-file-input"
                  files={claim.originalInvoiceFiles}
                  setFiles={claim.setOriginalInvoiceFiles}
                  errorMessage={claim.originalInvoiceErrorMessage}
                  forceValidation={forceValidation}
                  disabled={claimDisabled}
                />
              }
            />
            <ClaimsSubmitClaimSection
              index={4}
              title="Photos"
              description="If you have any photos of the freight or the damage, please upload them here."
              content={
                <ClaimsUploadClaimDocumentsSection
                  id="photos-file-input"
                  files={claim.photosFiles}
                  setFiles={claim.setPhotosFiles}
                  errorMessage={undefined}
                  forceValidation={forceValidation}
                  disabled={claimDisabled}
                />
              }
            />
            <ClaimsSubmitClaimSection
              index={5}
              title="Other Documents"
              description="Please upload any useful documents — an inspection report, a repair bill, or anything else that supports the claim"
              content={
                <ClaimsUploadClaimDocumentsSection
                  id="other-documents-file-input"
                  files={claim.otherDocumentsFiles}
                  setFiles={claim.setOtherDocumentsFiles}
                  errorMessage={undefined}
                  forceValidation={forceValidation}
                  disabled={claimDisabled}
                />
              }
            />
            <ClaimsSubmitClaimSection
              index={6}
              title="Description"
              description="Please tell us any relevant information that we should pass along to the carrier"
              content={
                <ShowFormErrors show={forceValidation}>
                  <FormItem
                    error={claim.descriptionErrorMessage}
                    disabled={claimDisabled}
                  >
                    <TextArea
                      data-testid="freight-claim-description-input"
                      rows={4}
                      width="2xl"
                      value={claim.description}
                      onChange={(e) =>
                        claim.setDescription(e.currentTarget.value)
                      }
                      disabled={claimDisabled}
                    />
                  </FormItem>
                </ShowFormErrors>
              }
            />
            <ClaimsSubmitClaimSection
              index={7}
              title="Other"
              description="If there is anything else we should know to assist making the claim. This information will NOT be shared with the carrier"
              content={
                <TextArea
                  data-testid="freight-claim-other-notes-input"
                  rows={4}
                  width="2xl"
                  value={claim.otherNotes}
                  onChange={(e) => claim.setOtherNotes(e.currentTarget.value)}
                  disabled={claimDisabled}
                />
              }
            />
          </Stack>

          {/* hideAccent: this is a total, not a notice — the blue rail made it
              read as a Banner flagging a problem. */}
          {totalClaim !== undefined && (
            <Callout hideAccent>
              <Row justify="between" grow>
                <T size="xl">Total Claim:</T>
                <T size="xl" weight="bold">
                  ${totalClaim.toLocaleString()} {currency}
                </T>
              </Row>
            </Callout>
          )}

          <Stack gap={4} align="stretch">
            {claim.uploadStatus && (
              <Banner variant="info" title={claim.uploadStatus}>
                Large files may take a minute or two to upload. Please do not
                close this page.
              </Banner>
            )}
            {forceValidation && claim.generalErrorMessage && (
              <Banner variant="danger" title="Your claim isn’t ready to send">
                {claim.generalErrorMessage}
              </Banner>
            )}
            {forceValidation && claim.openClaimErrorMessage && (
              <Banner variant="danger" title="We couldn’t submit your claim">
                {claim.openClaimErrorMessage}
              </Banner>
            )}

            <Row gap={6} justify="end" grow>
              <Button
                size="link"
                href={`/view-shipment?shipmentId=${shipmentId}`}
              >
                Cancel
              </Button>
              <Button
                primary
                data-testid="submit-claim-button"
                onClick={claim.onSubmitClaim}
                size="large"
                disabled={claim.isUploading || claimDisabled}
                disabledMessage={
                  claimDisabled
                    ? "A claim already exists for this shipment"
                    : "Upload is in progress"
                }
                loading={claim.isUploading}
                icon={ArrowRightIcon}
              >
                Submit Claim
              </Button>
            </Row>
          </Stack>
        </Stack>
      </ClaimsFormLayout>
    </>
  );
}
